import * as React from 'react';
import { Link } from 'react-router';
import styled, { keyframes } from 'styled-components';
import { rem, rgba } from 'polished';
import { TextP } from '../../Defaults/Text';
import {
  FlexColCenter,
  FlexCol,
  FlexRowCenterVertical,
  FlexBetween,
  FlexRowCenter,
} from '../../Defaults/Flexbox/Flexbox';
import { Tag, TagType } from './ProductTag';
import { ButtonLink } from '../General/Button';
import { StockInfo } from './StockInfo';
import {
  getProductDetailInfo,
  getElasticCategoryProductInfo,
} from '../../../utilities/selectors';
import { getZlatapriadkaProductDetailInfo } from '../selectors';
import { prop } from '../../../utilities';
import { FavoriteCompare } from './FavoriteCompare';
import { Picture } from '../General/Picture';

export interface Props {
  product: any;
  isElasticCategory: boolean;
  toggleFavourite: any;
  favouriteProducts?: any;
  isFavourite?: boolean;
  isAccountFavourite?: boolean;
}

export function ProductTabDefault({
  product,
  isElasticCategory,
  toggleFavourite,
  favouriteProducts,
  isFavourite,
  isAccountFavourite,
}: Props) {
  const { onStock, url, price, oldPrice } = isElasticCategory
    ? getZlatapriadkaProductDetailInfo(product)
    : getProductDetailInfo(product);

  const { name, issale, isnew } = isElasticCategory
    ? getElasticCategoryProductInfo(product)
    : getProductDetailInfo(product);

  const productDescription = desc => {
    return desc.replace(/style=".*?"/i, '').substring(0, 40);
  };

  const { image, picture } =
    isAccountFavourite || isElasticCategory
      ? getZlatapriadkaProductDetailInfo(product)
      : getProductDetailInfo(product);

  const { shortDescription } = getProductDetailInfo(product);

  const isTag = isnew === 1 || issale === 1;

  const [isHovered, setIsHovered] = React.useState(false);

  const onHover = () => {
    setIsHovered(true);
  };

  const onLeave = () => {
    setIsHovered(false);
  };

  const [isInFavourites, setIsInFavourites] = React.useState(
    (favouriteProducts && favouriteProducts.includes(product.product_id)) ||
      isFavourite,
  );

  const toggleFavouriteProductDetail = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsInFavourites(val => !val);
  };

  const firstUpdate = React.useRef(true);
  React.useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    toggleFavourite(product.product_id, isInFavourites);
  }, [isInFavourites]);

  return (
    <Wrapper to={url} onMouseEnter={onHover} onMouseLeave={onLeave}>
      <TopWrapper>
        <TagsWrapper>
          {isTag ? (
            <>
              {isnew ? <Tag type={TagType.NEW} text={'Novinka'} /> : null}
              {issale ? (
                <Tag
                  style={{ marginLeft: isnew ? rem(8) : 0 }}
                  type={TagType.ACTION}
                  text={'Akcia'}
                />
              ) : null}
            </>
          ) : null}
        </TagsWrapper>
        <FavoriteCompareWrapper>
          <FavoriteCompare
            isFavorite={isInFavourites}
            isHovered={isHovered}
            toggleFavourite={toggleFavouriteProductDetail}
          />
        </FavoriteCompareWrapper>
      </TopWrapper>
      <MainContent>
        <Picture
          alt={`Obrázok ku produktu ${name}`}
          image={picture}
          width={200}
          height={200}
        />
        {/* <Image alt={`Obrázok ku produktu ${name}`} src={image} /> */}
        <Name>{name}</Name>
        {shortDescription && shortDescription !== '' && (
          <Description
            dangerouslySetInnerHTML={{
              __html: productDescription(shortDescription),
            }}
          />
        )}
      </MainContent>
      <BottomWrapper>
        <PriceWrapper>
          <Price>{`${price} €`}</Price>
          {issale && oldPrice ? <OldPrice>{`${oldPrice} €`}</OldPrice> : null}
        </PriceWrapper>
        {!issale && !oldPrice && <StockInfo onStock={onStock} />}
      </BottomWrapper>
    </Wrapper>
  );
}

export const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  text-decoration: none;
  // animation: ${animateIn} 0.3s ease;
  position: relative;
  width: 100%;
  /* width: ${rem(345)}; */
  /* max-width: ${rem(328)}; */
  /* width: 33.3333%; */
  /* margin-right: ${rem(32)}; */
  padding: ${rem(24)};
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: transparent;
  border-radius: ${rem(8)};
  overflow: hidden;

  &:nth-child(3n) {
    margin-right: 0;
  }

  &:hover,
  &:focus {
    box-shadow: 0 ${rem(2)} ${rem(32)} #0000000A;
  }

  ${({ theme }) => theme.mediab.m580`
    padding: 0;
    
    &:hover,
    &:focus {
      box-shadow: none !important;
    }
  `}
`;

const TopWrapper = styled.div`
  position: relative;
  top: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  text-decoration: none;
  margin-bottom: 0;
`;

const TagsWrapper = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
`;

const FavoriteCompareWrapper = styled.div``;

const MainContent = styled(FlexColCenter)`
  margin-bottom: auto;
`;

const BottomWrapper = styled(FlexRowCenter)`
  justify-content: center;
  margin-top: auto;

  ${({ theme }) => theme.mediab.m580`
    flex-direction: column;
  `}
`;

const Image = styled.img`
  margin-bottom: ${rem(24)};
  width: ${rem(200)};
  height: ${rem(200)};
  object-fit: cover;

  ${({ theme }) => theme.mediab.m580`
    width: ${rem(120)};
    height: ${rem(120)};
  `}
`;

const Name = styled(TextP)`
  font-size: ${rem(16)};
  line-height: ${rem(24)};
  font-family: ${({ theme }) => theme.font.primary};
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: center;
  margin: 0 auto;
  margin-bottom: ${rem(16)};

  ${({ theme }) => theme.mediab.m580`
    font-size: ${rem(14)};
    line-height: ${rem(21)};
  `}
`;

const Description = styled.p`
  font-size: ${rem(14)};
  line-height: ${rem(17)};
  text-align: center;
  color: ${({ theme }) => rgba(theme.colors.textPrimary, 0.32)};
  margin-top: 0;
  margin-bottom: ${rem(24)};

  ${({ theme }) => theme.mediab.m580`
    font-size: ${rem(12)};
    line-height: ${rem(18)};
    margin-bottom: ${rem(16)};
  `}
`;

const PriceWrapper = styled(FlexRowCenter)`
  margin-right: ${rem(16)};
  flex-wrap: wrap;

  ${({ theme }) => theme.mediab.m580`
    margin-right: 0;
    margin-bottom: ${rem(17)};
    flex-direction: column;
  `}
`;

const Price = styled(TextP)`
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(24)};
  font-weight: 500;
  line-height: ${rem(28)};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;

  ${({ theme }) => theme.mediab.m580`
    font-size: ${rem(16)};
    line-height: ${rem(24)};
  `}
`;

const OldPrice = styled.span`
  margin-left: ${rem(16)};
  font-size: ${rem(14)};
  font-weight: 500;
  line-height: ${rem(17)};
  text-decoration: line-through;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.danger};

  ${({ theme }) => theme.mediab.m580`
    font-size: ${rem(14)};
    line-height: 1;
    margin-top: ${rem(8)};
    margin-left: 0;
  `}
`;
